import React from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplayIcon from "@mui/icons-material/Replay";
import styles from "./Settings.module.css";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEquipmentConfigsRequest,
  setEquipmentConfigFilters,
  createEquipmentConfigRequest,
  updateEquipmentConfigRequest,
  deleteEquipmentConfigRequest,
} from "../../../dux/equipmentConfig";
import { isApiPending } from "../../../dux/apiStatus";
import { LOCATIONS } from "../../../config/locations";

const SPORT_OPTIONS = [
  { value: "football", label: "Football" },
  { value: "box-cricket", label: "Box Cricket" },
  { value: "cricket-net", label: "Cricket Net" },
  { value: "pickle-ball", label: "Pickle Ball" },
];

const EQUIPMENT_NAME_OPTIONS = [
  "Paddle",
  "Ball",
  "Racket",
  "Shuttlecock",
  "Net",
  "Shoes",
  "Gloves",
  "Protective Gear",
];

const EQUIPMENT_TYPE_OPTIONS = [
  "rental",
  "purchase",
  "mandatory",
  "optional",
  "safety",
  "accessory",
];

const buildLocationOptions = () =>
  Object.entries(LOCATIONS).map(([value, loc]) => ({
    value,
    label: loc.displayName || loc.name,
  }));

const emptyFormState = {
  sport: "",
  location: "",
  equipment_name: "",
  equipment_type: "",
  max_quantity_per_court: 0,
  unit_price: 0,
  is_required: false,
  is_active: true,
  description: "",
};

const EquipmentConfigTab = React.memo(function EquipmentConfigTab({
  selectedLocation,
  isSuperAdmin,
}) {
  const dispatch = useDispatch();
  const { configs, filters, error } = useSelector(
    (state) => state.equipmentConfig
  );
  const listLoading = useSelector((state) =>
    isApiPending(state, "fetchEquipmentConfigs")
  );
  const createLoading = useSelector((state) =>
    isApiPending(state, "createEquipmentConfig")
  );
  const updateLoading = useSelector((state) =>
    isApiPending(state, "updateEquipmentConfig")
  );
  const deleteLoading = useSelector((state) =>
    isApiPending(state, "deleteEquipmentConfig")
  );
  const createError = useSelector((state) => state.equipmentConfig.error.create);
  const updateError = useSelector((state) => state.equipmentConfig.error.update);
  const deleteError = useSelector((state) => state.equipmentConfig.error.delete);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingConfig, setEditingConfig] = React.useState(null);
  const [formData, setFormData] = React.useState(emptyFormState);
  const [formErrors, setFormErrors] = React.useState({});
  const [submitMode, setSubmitMode] = React.useState(null);
  const [submitError, setSubmitError] = React.useState("");
  const [confirmState, setConfirmState] = React.useState({
    open: false,
    config: null,
    action: "",
  });
  const userTouchedLocationRef = React.useRef(false);

  const locationOptions = React.useMemo(buildLocationOptions, []);

  React.useEffect(() => {
    if (selectedLocation && !filters.location && !userTouchedLocationRef.current) {
      dispatch(setEquipmentConfigFilters({ location: selectedLocation }));
    }
  }, [selectedLocation, filters.location, dispatch]);

  React.useEffect(() => {
    dispatch(fetchEquipmentConfigsRequest(filters));
  }, [dispatch, filters.sport, filters.location, filters.include_inactive]);

  React.useEffect(() => {
    if (submitMode === "create" && !createLoading) {
      if (createError) {
        setSubmitError(createError);
      } else if (dialogOpen) {
        setDialogOpen(false);
      }
      setSubmitMode(null);
    }
  }, [submitMode, createLoading, createError, dialogOpen]);

  React.useEffect(() => {
    if (submitMode === "update" && !updateLoading) {
      if (updateError) {
        setSubmitError(updateError);
      } else if (dialogOpen) {
        setDialogOpen(false);
      }
      setSubmitMode(null);
    }
  }, [submitMode, updateLoading, updateError, dialogOpen]);

  React.useEffect(() => {
    if (submitMode === "delete" && !deleteLoading) {
      if (deleteError) {
        setSubmitError(deleteError);
      } else if (confirmState.open) {
        setConfirmState({ open: false, config: null, action: "" });
      }
      setSubmitMode(null);
    }
  }, [submitMode, deleteLoading, deleteError, confirmState.open]);

  const handleFilterChange = (field, value) => {
    if (field === "location") {
      userTouchedLocationRef.current = true;
    }
    dispatch(setEquipmentConfigFilters({ [field]: value }));
  };

  const openCreateDialog = () => {
    setEditingConfig(null);
    setFormErrors({});
    setSubmitError("");
    setFormData({
      ...emptyFormState,
      location: filters.location || selectedLocation || "",
    });
    setDialogOpen(true);
  };

  const openEditDialog = (config) => {
    setEditingConfig(config);
    setFormErrors({});
    setSubmitError("");
    setFormData({
      sport: config.sport || "",
      location: config.location || "",
      equipment_name: config.equipment_name || "",
      equipment_type: config.equipment_type || "",
      max_quantity_per_court: config.max_quantity_per_court || 0,
      unit_price: config.unit_price || 0,
      is_required: Boolean(config.is_required),
      is_active: Boolean(config.is_active),
      description: config.description || "",
    });
    setDialogOpen(true);
  };

  const openConfirmDialog = (config, action) => {
    setSubmitError("");
    setConfirmState({ open: true, config, action });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.sport) errors.sport = "Sport is required";
    if (!formData.location) errors.location = "Location is required";
    if (!formData.equipment_name)
      errors.equipment_name = "Equipment name is required";
    if (!formData.equipment_type)
      errors.equipment_type = "Equipment type is required";
    if (formData.max_quantity_per_court < 0)
      errors.max_quantity_per_court = "Max quantity must be 0 or more";
    if (formData.unit_price < 0) errors.unit_price = "Unit price must be 0+";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    setSubmitError("");

    const payload = {
      equipment_type: formData.equipment_type,
      max_quantity_per_court: Number(formData.max_quantity_per_court),
      unit_price: Number(formData.unit_price),
      is_required: Boolean(formData.is_required),
      is_active: Boolean(formData.is_active),
      description: formData.description || undefined,
    };

    if (editingConfig) {
      dispatch(updateEquipmentConfigRequest(editingConfig.id, payload));
      setSubmitMode("update");
      return;
    }

    dispatch(
      createEquipmentConfigRequest({
        sport: formData.sport,
        location: formData.location,
        equipment_name: formData.equipment_name,
        ...payload,
      })
    );
    setSubmitMode("create");
  };

  const handleConfirmAction = () => {
    if (!confirmState.config) return;
    setSubmitError("");
    if (confirmState.action === "deactivate") {
      dispatch(deleteEquipmentConfigRequest(confirmState.config.id));
    } else if (confirmState.action === "reactivate") {
      dispatch(
        updateEquipmentConfigRequest(confirmState.config.id, {
          is_active: true,
        })
      );
    }
    setSubmitMode("delete");
  };

  return (
    <Box>
      <Box className={styles.sectionHeader}>
        <Typography variant="h6" className={styles.sectionTitle}>
          Equipment Configs
        </Typography>
        <Box className={styles.actionButtonsContainer}>
          <Button
            onClick={openCreateDialog}
            className={styles.primaryButton}
            startIcon={<AddIcon />}
            disabled={!isSuperAdmin}
          >
            Create Config
          </Button>
        </Box>
      </Box>

      <Paper className={styles.searchFilterContainer}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Sport</InputLabel>
          <Select
            value={filters.sport || ""}
            label="Sport"
            onChange={(e) => handleFilterChange("sport", e.target.value)}
          >
            <MenuItem value="">All Sports</MenuItem>
            {SPORT_OPTIONS.map((sport) => (
              <MenuItem key={sport.value} value={sport.value}>
                {sport.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Location</InputLabel>
          <Select
            value={filters.location || ""}
            label="Location"
            onChange={(e) => handleFilterChange("location", e.target.value)}
          >
            <MenuItem value="">All Locations</MenuItem>
            {locationOptions.map((loc) => (
              <MenuItem key={loc.value} value={loc.value}>
                {loc.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(filters.include_inactive)}
              onChange={(e) =>
                handleFilterChange("include_inactive", e.target.checked)
              }
            />
          }
          label="Include Inactive"
        />
      </Paper>

      {error.list && (
        <Alert severity="error" className={styles.marginBottom2}>
          {error.list}
        </Alert>
      )}

      <TableContainer component={Paper} className={styles.tableContainer}>
        <Table className={styles.table}>
            <TableHead>
              <TableRow>
                <TableCell>Sport</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Equipment</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Max Qty</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Required</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.length === 0 && !listLoading && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="textSecondary">
                      No equipment configs found for the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {configs.map((config) => (
                <TableRow key={config.id}>
                  <TableCell>
                    {SPORT_OPTIONS.find((s) => s.value === config.sport)?.label ||
                      config.sport}
                  </TableCell>
                  <TableCell>
                    {locationOptions.find((l) => l.value === config.location)
                      ?.label || config.location}
                  </TableCell>
                  <TableCell>{config.equipment_name}</TableCell>
                  <TableCell>{config.equipment_type}</TableCell>
                  <TableCell>{config.max_quantity_per_court}</TableCell>
                  <TableCell>₹{config.unit_price}</TableCell>
                  <TableCell>
                    <Chip
                      label={config.is_required ? "Required" : "Optional"}
                      size="small"
                      color={config.is_required ? "warning" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.is_active ? "Active" : "Inactive"}
                      size="small"
                      color={config.is_active ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => openEditDialog(config)}
                      disabled={!isSuperAdmin}
                    >
                      Edit
                    </Button>
                    {config.is_active ? (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => openConfirmDialog(config, "deactivate")}
                        disabled={!isSuperAdmin}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        color="success"
                        startIcon={<ReplayIcon />}
                        onClick={() => openConfirmDialog(config, "reactivate")}
                        disabled={!isSuperAdmin}
                      >
                        Reactivate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {listLoading && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingConfig ? "Edit Equipment Config" : "Create Equipment Config"}
        </DialogTitle>
        <DialogContent dividers>
          {submitError && (
            <Alert severity="error" className={styles.marginBottom2}>
              {submitError}
            </Alert>
          )}
          <Box display="grid" gap={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sport</InputLabel>
              <Select
                value={formData.sport}
                label="Sport"
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, sport: e.target.value }))
                }
                disabled={Boolean(editingConfig)}
                error={Boolean(formErrors.sport)}
              >
                {SPORT_OPTIONS.map((sport) => (
                  <MenuItem key={sport.value} value={sport.value}>
                    {sport.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Location</InputLabel>
              <Select
                value={formData.location}
                label="Location"
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, location: e.target.value }))
                }
                disabled={Boolean(editingConfig)}
                error={Boolean(formErrors.location)}
              >
                {locationOptions.map((loc) => (
                  <MenuItem key={loc.value} value={loc.value}>
                    {loc.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Equipment Name</InputLabel>
              <Select
                value={formData.equipment_name}
                label="Equipment Name"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    equipment_name: e.target.value,
                  }))
                }
                disabled={Boolean(editingConfig)}
                error={Boolean(formErrors.equipment_name)}
              >
                {EQUIPMENT_NAME_OPTIONS.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Equipment Type</InputLabel>
              <Select
                value={formData.equipment_type}
                label="Equipment Type"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    equipment_type: e.target.value,
                  }))
                }
                error={Boolean(formErrors.equipment_type)}
              >
                {EQUIPMENT_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Max Quantity Per Court"
              type="number"
              value={formData.max_quantity_per_court}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  max_quantity_per_court: e.target.value,
                }))
              }
              error={Boolean(formErrors.max_quantity_per_court)}
              helperText={formErrors.max_quantity_per_court}
              fullWidth
              size="small"
              inputProps={{ min: 0 }}
            />

            <TextField
              label="Unit Price (₹)"
              type="number"
              value={formData.unit_price}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  unit_price: e.target.value,
                }))
              }
              error={Boolean(formErrors.unit_price)}
              helperText={formErrors.unit_price}
              fullWidth
              size="small"
              inputProps={{ min: 0 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData.is_required)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_required: e.target.checked,
                    }))
                  }
                />
              }
              label="Required"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData.is_active)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                />
              }
              label="Active"
            />

            <TextField
              label="Description (optional)"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createLoading || updateLoading}
          >
            {editingConfig ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Deactivate / Reactivate */}
      <Dialog
        open={confirmState.open}
        onClose={() => setConfirmState({ open: false, config: null, action: "" })}
      >
        <DialogTitle>
          {confirmState.action === "reactivate"
            ? "Reactivate Equipment Config"
            : "Deactivate Equipment Config"}
        </DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" className={styles.marginBottom2}>
              {submitError}
            </Alert>
          )}
          <Typography variant="body2">
            {confirmState.action === "reactivate"
              ? "Reactivate this equipment config? It will appear in booking options."
              : "Deactivate this equipment config? It will no longer appear in booking options."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmState({ open: false, config: null, action: "" })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={confirmState.action === "reactivate" ? "success" : "error"}
            disabled={deleteLoading}
          >
            {confirmState.action === "reactivate" ? "Reactivate" : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default EquipmentConfigTab;
